import React from 'react';
import { Trash2, Pencil, Clock, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { formatIncomeDateTime } from './incomeFormUtils';

const downloadReceipt = (item) => {
  if (!item.receipt_image) { toast.error('No receipt on this entry'); return; }
  const a = document.createElement('a');
  a.href = item.receipt_image;
  a.download = item.document_name || `receipt_${item.id.slice(0, 8)}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const IncomeTable = ({ income, projects, loading, periodLabel, canEdit, onEdit, onDelete }) => (
  <Card>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date & Time</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Related Project</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="w-16">Receipt</TableHead>
            {canEdit && <TableHead className="w-12"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8">
                Loading...
              </TableCell>
            </TableRow>
          ) : income.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No income records found for {periodLabel}
              </TableCell>
            </TableRow>
          ) : (
            income.map((item) => {
              const { date, time } = formatIncomeDateTime(item.date, item.created_at);
              const projectName = item.related_project ? (projects.find((p) => p.id === item.related_project)?.name || '—') : null;
              return (
                <TableRow key={item.id} className="table-row-hover">
                  <TableCell>
                    <div>
                      <p className="font-medium">{date}</p>
                      {time && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {time}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {item.source.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.description || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {projectName ? <Badge variant="secondary">{projectName}</Badge> : <span className="text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    {formatCurrency(item.amount)}
                  </TableCell>
                  <TableCell>
                    {item.receipt_image ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => downloadReceipt(item)}
                        title="Download receipt/voucher"
                        data-testid={`download-receipt-${item.id}`}
                      >
                        <Receipt className="h-4 w-4 text-blue-600" />
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(item)}
                          data-testid={`edit-income-${item.id}`}
                          className="h-8 px-2.5"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDelete(item)}
                          data-testid={`delete-income-${item.id}`}
                          className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);
